import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { lastValueFrom, of } from 'rxjs';
import { catchError, tap, timeout } from 'rxjs/operators';
import * as Sentry from '@sentry/angular-ivy';
import { environment } from 'src/environments/environment';
import { LocalStorageHelper } from '../../util/local-storage-helper';

export interface GeoInfo {
  ip?: string;
  geo?: {
    country?: string;
  };
}

/**
 * Service for fetching and caching user's geographic location information.
 * Only works on allowed TimeDoctor domains for security reasons.
 *
 * Features:
 * - Caches geo info in localStorage for 7 days
 * - Domain validation for security
 * - Graceful error handling
 * - Singleton pattern to prevent multiple API calls
 */
@Injectable({ providedIn: 'root' })
export class GeoLocationService {
  private geoInfo: Promise<GeoInfo> | null = null;
  private readonly allowedDomains = ['timedoctor.com', 'timedoctortest.com', 'timedoctordev.com'];
  private readonly cacheKey = 'geo-info';
  private readonly cacheExpiryDays = 7;
  private readonly requestTimeout = 5000; // 5 seconds

  constructor(private readonly httpClient: HttpClient) {}

  /**
   * Gets the user's geographic information.
   * Uses smart cache validation: checks if cached location matches current location.
   *
   * @returns Promise<GeoInfo> - Geographic information or empty object if unavailable
   */
  getGeoInfo(): Promise<GeoInfo> {
    // Return existing promise if already fetching
    if (this.geoInfo !== null) {
      return this.geoInfo;
    }

    // Check if domain is allowed
    if (!this.isDomainAllowed()) {
      this.geoInfo = Promise.resolve({});
      return this.geoInfo;
    }

    // Check cache first
    const cachedGeoInfo = this.getCachedGeoInfo();
    if (cachedGeoInfo?.ip) {
      // Cache exists - validate it against fresh data
      this.geoInfo = this.validateAndUseCache(cachedGeoInfo);
      return this.geoInfo;
    }

    // No cache - fetch fresh
    this.geoInfo = this.fetchGeoInfoFromApi();
    return this.geoInfo;
  }

  /**
   * Checks if the current domain is allowed to make geo-location requests
   */
  private isDomainAllowed(): boolean {
    const currentDomain = this.getCurrentDomain();
    return this.allowedDomains.includes(currentDomain);
  }

  /**
   * Gets the current domain, handling localhost and vercel deployments
   */
  private getCurrentDomain(): string {
    const hostname = globalThis.location.hostname;

    // Handle localhost and vercel deployments
    if (hostname.includes('localhost') || hostname.includes('timedoctor.vercel.app')) {
      return 'timedoctortest.com';
    }

    // Extract main domain from hostname
    const parts = hostname.split('.');
    if (parts.length >= 2) {
      // Get the last two parts (e.g., 'timedoctor.com' from 'app.timedoctor.com')
      return parts.slice(-2).join('.');
    }

    return hostname;
  }

  /**
   * Gets cached geo info from localStorage
   */
  getCachedGeoInfo(): GeoInfo | null {
    return LocalStorageHelper.getItemWithExpiry<GeoInfo>(this.cacheKey);
  }

  /**
   * Validates cached geo info against fresh data and updates cache if locations differ
   */
  private async validateAndUseCache(cachedGeoInfo: GeoInfo): Promise<GeoInfo> {
    try {
      // Fetch fresh data to validate against cache
      const freshGeoInfo = await this.fetchGeoInfoFromApi();

      if (this.isSameLocation(cachedGeoInfo, freshGeoInfo)) {
        // Locations match - refresh cache timestamp and use cache
        this.refreshCacheTimestamp();
        return cachedGeoInfo;
      } else {
        // Locations differ - use fresh data (already cached by fetchGeoInfoFromApi)
        Sentry.captureMessage('Geo-location cache updated due to location change', {
          level: 'info',
          extra: {
            cachedCountry: cachedGeoInfo?.geo?.country,
            freshCountry: freshGeoInfo?.geo?.country,
          },
        });
        return freshGeoInfo;
      }
    } catch (error) {
      // API failed - fall back to cache
      Sentry.captureMessage('Geo-location cache validation failed, using cached data', {
        level: 'warning',
        extra: { error: error?.message || error },
      });
      return cachedGeoInfo;
    }
  }

  /**
   * Checks if two geo info objects represent the same location
   */
  private isSameLocation(cached: GeoInfo, fresh: GeoInfo): boolean {
    return cached?.geo?.country === fresh?.geo?.country;
  }

  /**
   * Refreshes the cache timestamp without changing the data
   */
  private refreshCacheTimestamp(): void {
    const cachedGeoInfo = this.getCachedGeoInfo();
    if (cachedGeoInfo?.ip) {
      LocalStorageHelper.setItemWithExpiry(this.cacheKey, cachedGeoInfo, this.cacheExpiryDays);
    }
  }

  /**
   * Fetches geo info from the API with proper error handling
   */
  private fetchGeoInfoFromApi(): Promise<GeoInfo> {
    return lastValueFrom(
      this.httpClient.get<GeoInfo>(environment.geoInfoApiUrl).pipe(
        timeout(this.requestTimeout),
        tap(data => {
          // Cache successful response
          LocalStorageHelper.setItemWithExpiry(this.cacheKey, data, this.cacheExpiryDays);
        }),
        catchError(error => {
          // Log error for debugging but don't throw
          Sentry.captureMessage('Failed to fetch geo info from API', {
            level: 'warning',
            extra: { error: error?.message || error },
          });
          return of({} as GeoInfo);
        }),
      ),
    );
  }
}
